import { UserGroup, UserGroupParams } from '@admin/api/userGroups';
import WaveTextArea from '@common/components/form/WaveTextArea';
import CloseIcon from '@mui/icons-material/Close';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import React, { useState } from 'react';

const DESCRIPTION_MAX_LENGTH = 1000;

interface UserGroupModalProps {
  open: boolean;
  // The group being edited, or null when adding a new one.
  userGroup: UserGroup | null;
  onClose: () => void;
  onSave: (params: UserGroupParams) => void;
  isPending?: boolean;
  // Validation errors from the backend, keyed by field.
  fieldErrors?: Record<string, string[]>;
}

interface UserGroupFormProps {
  userGroup: UserGroup | null;
  onSave: (params: UserGroupParams) => void;
  isPending: boolean;
  fieldErrors: Record<string, string[]>;
}

// Lives inside the Dialog, which unmounts its content when closed, so the form
// state is initialised fresh from the selected group each time the modal opens.
const UserGroupForm = ({ userGroup, onSave, isPending, fieldErrors }: UserGroupFormProps) => {
  const [name, setName] = useState(userGroup?.name ?? '');
  const [description, setDescription] = useState(userGroup?.description ?? '');

  const handleSave = () => {
    onSave({ name: name.trim(), description: description.trim() || null });
  };

  return (
    <DialogContent>
      <TextField
        autoFocus
        error={!!fieldErrors.name}
        fullWidth
        helperText={fieldErrors.name?.[0]}
        inputProps={{ maxLength: 255 }}
        label="Name"
        margin="dense"
        onChange={(event) => setName(event.target.value)}
        required
        value={name}
        variant="standard"
      />
      <Box sx={{ mt: 2 }}>
        <WaveTextArea
          error={!!fieldErrors.description}
          helperText={fieldErrors.description?.[0]}
          label="Description"
          maxLength={DESCRIPTION_MAX_LENGTH}
          onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) => setDescription(event.target.value)}
          rows={4}
          value={description}
        />
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
        <Button
          color="secondary"
          disabled={isPending || !name.trim()}
          onClick={handleSave}
          size="small"
          variant="contained"
        >
          <Typography variant="button">Save</Typography>
        </Button>
      </Box>
    </DialogContent>
  );
};

const UserGroupModal = ({
  open,
  userGroup,
  onClose,
  onSave,
  isPending = false,
  fieldErrors = {}
}: UserGroupModalProps) => (
  <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
    <DialogTitle
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        color: 'primary.main'
      }}
    >
      {userGroup ? 'Edit user group' : 'Add user group'}
      <IconButton onClick={onClose} size="small">
        <CloseIcon />
      </IconButton>
    </DialogTitle>
    <UserGroupForm
      userGroup={userGroup}
      onSave={onSave}
      isPending={isPending}
      fieldErrors={fieldErrors}
    />
  </Dialog>
);

export default UserGroupModal;
